import DOMPurify from "dompurify";
import {
  CheckCircle2,
  Circle,
  AlertCircle,
  Clock,
  Calendar,
  Mail,
  Edit2,
  ChevronDown,
  ChevronUp,
  Plus,
  Send,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Badge } from "../ui/badge";
import { Label } from "../ui/label";
import {
  Select,
  SelectContent,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "../ui/collapsible";
import { format } from "date-fns";
import { RichTextEditor } from "../RichTextEditor";
import AddTaskForm from "../AddTaskForm";
import { InsertVariableBtn } from "./InsertVariableBtn";
import { AssigneeOptions } from "./AssigneeOptions";

const formatDateSafe = (dateStr, fmt = 'MMM d, yyyy') => {
  if (!dateStr) return '';
  try {
    const s = String(dateStr);
    const datePart = s.includes('T') ? s.split('T')[0] : s.split(' ')[0];
    const [y, m, d] = datePart.split('-').map(Number);
    if (!y || !m || !d) return '';
    return format(new Date(y, m - 1, d), fmt);
  } catch { return ''; }
};

const EMPTY_TASK = { title: '', due_date: '', assigned_to_type: '', description: '', task_type: 'task', email_subject: '', email_body: '', email_recipients: [], email_cc: '', email_bcc: '' };

export const TasksPanel = ({
  transaction,
  taskGroups,
  completedTasks,
  totalTasks,
  expandedGroups,
  setExpandedGroups,
  showAddTask,
  setShowAddTask,
  newTask,
  setNewTask,
  onAddTask,
  editingTaskId,
  editTaskData,
  setEditTaskData,
  onStartEditTask,
  onSaveEditTask,
  onTaskStatusChange,
  onEmailPreview,
  onSendReminder,
  isTaskOverdue,
  getAssigneeName,
  activeUsers,
  currentUserId,
  contacts,
}) => {
  const transactionPeople = [
    { name: transaction.buyer_name, email: transaction.buyer_email },
    { name: transaction.buyer2_name, email: transaction.buyer2_email },
    { name: transaction.seller_name, email: transaction.seller_email },
    { name: transaction.seller2_name, email: transaction.seller2_email },
    { name: transaction.buyer_agent_name, email: transaction.buyer_agent_email },
    { name: transaction.seller_agent_name, email: transaction.seller_agent_email },
    { name: transaction.lender_name, email: transaction.lender_email },
    { name: transaction.title_company_name, email: transaction.title_company_email },
    { name: transaction.loan_processor_name, email: transaction.loan_processor_email },
  ].filter(p => p.email);

  return (
    <Card className="card-surface" data-testid="tasks-card">
      <CardHeader className="border-b border-[var(--t-border)] py-4 px-5">
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg font-semibold text-[var(--t-text)] [font-family:var(--t-heading-font)]">
            Tasks
          </CardTitle>
          <div className="flex items-center gap-2">
            <Badge variant="outline">
              {completedTasks} / {totalTasks} completed
            </Badge>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowAddTask(!showAddTask)}
              className="h-7 text-xs"
              data-testid="add-task-btn"
            >
              <Plus className="w-3 h-3 mr-1" /> Add Task
            </Button>
          </div>
        </div>
        {totalTasks > 0 && (
          <div className="mt-3 h-2 bg-slate-100 rounded-full overflow-hidden">
            <div
              className="h-full bg-[var(--t-primary)] rounded-full transition-all"
              style={{ width: `${(completedTasks / totalTasks) * 100}%` }}
            />
          </div>
        )}
      </CardHeader>
      {showAddTask && (
        <AddTaskForm
          newTask={newTask}
          setNewTask={setNewTask}
          onSubmit={onAddTask}
          onCancel={() => { setShowAddTask(false); setNewTask(EMPTY_TASK); }}
          activeUsers={activeUsers}
          currentUserId={currentUserId}
          InsertVariableBtn={InsertVariableBtn}
          contacts={contacts}
          transactionPeople={transactionPeople}
        />
      )}
      <CardContent className="p-0">
        {Object.keys(taskGroups).length > 0 ? (
          <div className="divide-y divide-slate-100">
            {Object.entries(taskGroups).map(([groupName, tasks]) => {
              const openTasks = tasks.filter(t => t.status !== 'completed');
              const doneTasks = tasks.filter(t => t.status === 'completed');
              return (
                <Collapsible
                  key={groupName}
                  open={expandedGroups[groupName] !== false}
                  onOpenChange={(open) => setExpandedGroups({ ...expandedGroups, [groupName]: open })}
                >
                  <CollapsibleTrigger className="w-full flex items-center justify-between px-5 py-3 bg-[var(--t-bg)] hover:bg-[var(--t-border-light)] transition-colors">
                    <span className="text-sm font-semibold text-[var(--t-text)]">{groupName}</span>
                    <div className="flex items-center gap-2">
                      <Badge variant="outline" className="text-xs">
                        {doneTasks.length}/{tasks.length}
                      </Badge>
                      {expandedGroups[groupName] !== false ? (
                        <ChevronUp className="w-4 h-4 text-[var(--t-text-subtle)]" />
                      ) : (
                        <ChevronDown className="w-4 h-4 text-[var(--t-text-subtle)]" />
                      )}
                    </div>
                  </CollapsibleTrigger>
                  <CollapsibleContent>
                    {/* Open tasks */}
                    <div className="divide-y divide-slate-50">
                      {openTasks.map((task) => {
                        const overdue = isTaskOverdue(task);
                        const isEditingThis = editingTaskId === task.task_id;
                        const taskIdx = tasks.indexOf(task);

                        if (isEditingThis) {
                          return (
                            <div key={task.task_id} className="px-5 py-3 bg-[var(--t-bg)] space-y-2" data-testid={`task-edit-${taskIdx}`}>
                              <div className="grid grid-cols-1 md:grid-cols-4 gap-2">
                                <div className="md:col-span-2">
                                  <Input
                                    value={editTaskData.title}
                                    onChange={(e) => setEditTaskData({ ...editTaskData, title: e.target.value })}
                                    className="h-8 text-sm"
                                    data-testid={`edit-task-title-${taskIdx}`}
                                  />
                                </div>
                                <div>
                                  <Input
                                    type="date"
                                    value={editTaskData.due_date}
                                    onChange={(e) => setEditTaskData({ ...editTaskData, due_date: e.target.value })}
                                    className="h-8 text-sm"
                                    data-testid={`edit-task-date-${taskIdx}`}
                                  />
                                </div>
                                <div>
                                  <Select
                                    value={editTaskData.assigned_to_type || "none"}
                                    onValueChange={(v) => setEditTaskData({ ...editTaskData, assigned_to_type: v === "none" ? "" : v })}
                                  >
                                    <SelectTrigger className="h-8 text-sm" data-testid={`edit-task-role-${taskIdx}`}>
                                      <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                      <AssigneeOptions activeUsers={activeUsers} currentUserId={currentUserId} />
                                    </SelectContent>
                                  </Select>
                                </div>
                              </div>
                              <div>
                                <Label className="text-xs text-[var(--t-text-muted)] mb-1 block">Description</Label>
                                <RichTextEditor
                                  value={editTaskData.description || ""}
                                  onChange={(val) => setEditTaskData({ ...editTaskData, description: val })}
                                  placeholder="Task description..."
                                  minHeight="60px"
                                />
                              </div>
                              <div className="flex justify-end gap-2">
                                <Button variant="ghost" size="sm" onClick={() => onStartEditTask(null)} data-testid={`cancel-edit-task-${taskIdx}`}>
                                  Cancel
                                </Button>
                                <Button size="sm" onClick={() => onSaveEditTask(task.task_id)} className="bg-[var(--t-primary)] hover:bg-[var(--t-primary-hover)]" data-testid={`save-edit-task-${taskIdx}`}>
                                  Save
                                </Button>
                              </div>
                            </div>
                          );
                        }

                        return (
                          <div
                            key={task.task_id}
                            className={`flex items-start gap-4 px-5 py-4 ${overdue ? 'bg-red-50' : ''}`}
                            data-testid={`task-${taskIdx}`}
                          >
                            <button
                              onClick={() => onTaskStatusChange(task.task_id, 'completed')}
                              className="mt-0.5 flex-shrink-0"
                              data-testid={`task-toggle-${taskIdx}`}
                            >
                              {overdue ? (
                                <AlertCircle className="w-5 h-5 text-red-600" />
                              ) : (
                                <Circle className="w-5 h-5 text-slate-300 hover:text-[var(--t-text-subtle)]" />
                              )}
                            </button>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-[var(--t-text)]">
                                {task.title}
                              </p>
                              {task.description && (
                                <div className="text-xs text-[var(--t-text-muted)] mt-0.5 prose prose-xs max-w-none [&_p]:m-0" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(task.description) }} />
                              )}
                              <div className="flex items-center gap-2 mt-2">
                                <span className={`text-xs ${overdue ? 'text-red-600 font-medium' : 'text-[var(--t-text-muted)]'}`}>
                                  <Calendar className="w-3 h-3 inline mr-1" />
                                  {formatDateSafe(task.due_date, 'MMM d')}
                                </span>
                                {task.assigned_to_type && (
                                  <span className="text-xs text-[var(--t-text-subtle)] capitalize">
                                    • {getAssigneeName(task.assigned_to_type)}
                                  </span>
                                )}
                                {task.task_type === "email" && (
                                  <Badge variant={task.email_sent ? "default" : "outline"} className={`text-[10px] h-5 ${task.email_sent ? 'bg-green-100 text-green-700 border-green-200' : 'text-blue-600 border-blue-200'}`}>
                                    <Mail className="w-3 h-3 mr-1" />
                                    {task.email_sent ? "Sent" : "Email Ready"}
                                  </Badge>
                                )}
                              </div>
                            </div>
                            {task.task_type === "email" && !task.email_sent && (
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => onEmailPreview(task.task_id)}
                                className="text-blue-600 border-blue-200 hover:bg-blue-50 gap-1 text-xs shrink-0"
                                data-testid={`send-email-btn-${taskIdx}`}
                              >
                                <Send className="w-3.5 h-3.5" />
                                Send Email
                              </Button>
                            )}
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => onStartEditTask(task)}
                              className="text-[var(--t-text-subtle)] hover:text-[var(--t-text-muted)]"
                              data-testid={`edit-task-btn-${taskIdx}`}
                            >
                              <Edit2 className="w-4 h-4" />
                            </Button>
                            {task.email_reminder && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => onSendReminder(task.task_id)}
                                className="text-[var(--t-text-subtle)] hover:text-[var(--t-text-muted)]"
                                data-testid={`send-reminder-${taskIdx}`}
                              >
                                <Mail className="w-4 h-4" />
                              </Button>
                            )}
                          </div>
                        );
                      })}
                      {openTasks.length === 0 && doneTasks.length > 0 && (
                        <div className="px-5 py-3 text-center">
                          <p className="text-sm text-green-600 font-medium">All tasks in this group are completed</p>
                        </div>
                      )}
                    </div>
                    {/* Completed tasks folder */}
                    {doneTasks.length > 0 && (
                      <Collapsible
                        open={expandedGroups[`${groupName}_completed`] === true}
                        onOpenChange={(open) => setExpandedGroups({ ...expandedGroups, [`${groupName}_completed`]: open })}
                      >
                        <CollapsibleTrigger className="w-full flex items-center gap-2 px-5 py-2.5 bg-green-50/60 hover:bg-green-50 transition-colors border-t border-[var(--t-border)]" data-testid={`completed-folder-${groupName}`}>
                          <CheckCircle2 className="w-4 h-4 text-green-600 shrink-0" />
                          <span className="text-xs font-semibold text-green-700">
                            Completed ({doneTasks.length})
                          </span>
                          <div className="flex-1" />
                          {expandedGroups[`${groupName}_completed`] === true ? (
                            <ChevronUp className="w-3.5 h-3.5 text-green-600" />
                          ) : (
                            <ChevronDown className="w-3.5 h-3.5 text-green-600" />
                          )}
                        </CollapsibleTrigger>
                        <CollapsibleContent>
                          <div className="divide-y divide-slate-50 bg-green-50/30">
                            {doneTasks.map((task) => {
                              const taskIdx = tasks.indexOf(task);
                              return (
                                <div
                                  key={task.task_id}
                                  className="flex items-start gap-4 px-5 py-3"
                                  data-testid={`task-${taskIdx}`}
                                >
                                  <button
                                    onClick={() => onTaskStatusChange(task.task_id, 'pending')}
                                    className="mt-0.5 flex-shrink-0"
                                    data-testid={`task-toggle-${taskIdx}`}
                                  >
                                    <CheckCircle2 className="w-5 h-5 text-green-600" />
                                  </button>
                                  <div className="flex-1 min-w-0">
                                    <p className="text-sm text-[var(--t-text-subtle)] line-through">
                                      {task.title}
                                    </p>
                                    <div className="flex items-center gap-2 mt-1">
                                      <span className="text-xs text-[var(--t-text-subtle)]">
                                        <Calendar className="w-3 h-3 inline mr-1" />
                                        {formatDateSafe(task.due_date, 'MMM d')}
                                      </span>
                                      {task.assigned_to_type && (
                                        <span className="text-xs text-[var(--t-text-subtle)] capitalize">
                                          • {getAssigneeName(task.assigned_to_type)}
                                        </span>
                                      )}
                                      {task.completed_at && (
                                        <span className="text-xs text-green-600">
                                          • Done {formatDateSafe(task.completed_at, 'MMM d')}
                                        </span>
                                      )}
                                    </div>
                                  </div>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => onStartEditTask(task)}
                                    className="text-[var(--t-text-subtle)] hover:text-[var(--t-text-muted)]"
                                  >
                                    <Edit2 className="w-4 h-4" />
                                  </Button>
                                </div>
                              );
                            })}
                          </div>
                        </CollapsibleContent>
                      </Collapsible>
                    )}
                  </CollapsibleContent>
                </Collapsible>
              );
            })}
          </div>
        ) : (
          <div className="empty-state py-12">
            <Clock className="empty-state-icon" />
            <p className="empty-state-title">No tasks yet</p>
            <p className="empty-state-description">
              Apply a workflow template to add tasks to this transaction.
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
